import React, { useState, useEffect } from 'react';
import { Spinner } from 't-lib-ui/widgets';
import { Post } from '../schema-copy/feed-schema';
import { getPosts } from '../connector/post-engine';
import { formatPostDate, formatLastFetch, stripHtml, decodeEntities } from '../utils/formatting';
import SinglePostView from './SinglePostView';
import { useFeedNameOverride } from '../connector/feed-name-overrides';
import './FeedPreview.css';

// Small warning shown when the previewed feed hasn't been fetched in the last 2 hours,
// so the visible posts may lag behind the source. Renders nothing for fresh feeds.
export const StaleFetchWarning: React.FC<{ lastFetchedAt?: string }> = ({ lastFetchedAt }) => {
  const lastFetch = formatLastFetch(lastFetchedAt);
  if (!lastFetch) return null;
  return (
    <div className="feedPreviewStaleWarning">
      {lastFetch}. Content may not be up to date, but will be updated when followed.
    </div>
  );
};

interface FeedPreviewProps {
  feed: { id: string; postCount: number; title?: string; shortTitle?: string; siteUrl?: string; siteCss?: string | null; lastFetchedAt?: string };
  maxPosts?: number;
  // Admin-only: show each post's internal sequence number (e.g. for correlating a report's
  // "post #N" with the actual post). Off on public/user-facing previews.
  showPostNumber?: boolean;
}

const FeedPreview: React.FC<FeedPreviewProps> = ({ feed, maxPosts = 20, showPostNumber = false }) => {
  const [recentPosts, setRecentPosts] = useState<Post[]>([]);
  const [isLoadingPosts, setIsLoadingPosts] = useState(false);
  const [expandedPost, setExpandedPost] = useState<Post | null>(null);
  const customFeedName = useFeedNameOverride(feed.id);

  useEffect(() => {
    if (!feed || feed.postCount == 0) {
      setRecentPosts([]);
      setExpandedPost(null);
      return;
    }

    const loadPosts = async () => {
      setIsLoadingPosts(true);
      setExpandedPost(null);
      try {
        // Get the last N posts (or fewer if feed has less)
        const pc = feed.postCount;
        const count = Math.min(maxPosts, pc);
        const startIndex = Math.max(0, pc - count);
        const posts = await getPosts({ ...feed, postCount: pc } as any, startIndex, pc);

        // Reverse to show newest first
        setRecentPosts(posts.reverse());
      } catch (error) {
        console.error('Error loading posts:', error);
        setRecentPosts([]);
      } finally {
        setIsLoadingPosts(false);
      }
    };

    loadPosts();
  }, [feed, maxPosts]);

  if (isLoadingPosts) {
    return (
      <div className="feedPreviewLoading">
        <Spinner spinning={true} />
        <span>Loading recent posts...</span>
      </div>
    );
  }

  if (!recentPosts.length) {
    return <div className="feedPreviewEmpty">Preview not available yet.</div>;
  }

  if (expandedPost) {
    const backBtn = (
      <button className="feedPreviewBack" onClick={() => setExpandedPost(null)}>
        ← Back to list
      </button>
    );
    return (
      <div className="feedPreview feedPreviewExpanded">
        {backBtn}
        <SinglePostView
          post={expandedPost}
          feedId={feed.id}
          feedName={customFeedName || feed.shortTitle || feed.title}
          siteUrl={feed.siteUrl}
          siteCss={feed.siteCss}
        />
        {backBtn}
      </div>
    );
  }

  return (
    <div className="feedPreview">
      <h3 className="feedPreviewTitle">Recent Posts</h3>
      <StaleFetchWarning lastFetchedAt={feed.lastFetchedAt} />
      <div className="feedPreviewList">
        {recentPosts.map((post, index) => {
          return (
            <div key={post.number} className="feedPreviewPost feedPreviewPostClickable" onClick={() => setExpandedPost(post)}>
              <div className="feedPreviewPostHeader">
                {showPostNumber && <span className="feedPreviewPostNumber">#{post.number}</span>}
                <h4 className="feedPreviewPostTitle">{decodeEntities(post.title) || 'Untitled'}</h4>
                <span className="feedPreviewPostDate">{formatPostDate(post.pubDate)}</span>
              </div>
              {post.description && (
                <p className="feedPreviewPostDescription">
                  {stripHtml(post.description, 1000)}
                </p>
              )}
              <button
                className="feedPreviewShowPost"
                onClick={e => { e.stopPropagation(); e.preventDefault(); setExpandedPost(post); }}
              >
                Show post →
              </button>
              {index < recentPosts.length - 1 && <div className="feedPreviewPostDivider" />}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default FeedPreview;
